import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  VStack,
  HStack,
  Text,
  Input,
  Textarea,
  useToast,
  useColorModeValue,
  Box,
  Divider
} from '@chakra-ui/react';
import IconWrapper from './IconWrapper';
import { FiUpload, FiVideo, FiImage, FiFileText } from 'react-icons/fi';
import { uploadFileDirect } from '../services/uploadService';
import { updateContent } from '../services/contentUpdateService';

interface Episode {
  id: string;
  title: string;
  description: string;
  seasonNumber?: string | number | undefined;
  episodeNumber?: string | number | undefined;
  duration?: number;
  videoUrl?: string;
  thumbnailUrl?: string;
  subtitleUrl?: string;
  assets?: {
    video?: string;
    thumbnail?: string;
    subtitle?: string;
  };
}

interface EditEpisodeModalProps {
  isOpen: boolean;
  onClose: () => void;
  episode: Episode | null;
  contentId: string;
  userId: string;
  onSuccess: () => void;
}

export default function EditEpisodeModal({
  isOpen,
  onClose,
  episode,
  contentId,
  userId,
  onSuccess
}: EditEpisodeModalProps) {
  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    seasonNumber: 1,
    episodeNumber: 1,
    duration: 0
  });
  const [videoFile, setVideoFile] = useState<File | null>(null);
  const [thumbnailFile, setThumbnailFile] = useState<File | null>(null);
  const [subtitleFile, setSubtitleFile] = useState<File | null>(null);
  
  const toast = useToast();
  const borderColor = useColorModeValue('gray.300', 'gray.600');
  const inputBg = useColorModeValue('gray.50', 'gray.700');

  useEffect(() => {
    if (episode) {
      setFormData({
        title: episode.title || '',
        description: episode.description || '',
        seasonNumber: episode.seasonNumber 
          ? (typeof episode.seasonNumber === 'string' ? parseInt(episode.seasonNumber) || 1 : episode.seasonNumber)
          : 1,
        episodeNumber: episode.episodeNumber 
          ? (typeof episode.episodeNumber === 'string' ? parseInt(episode.episodeNumber) || 1 : episode.episodeNumber)
          : 1,
        duration: episode.duration || 0
      });
    }
  }, [episode]);

  const handleInputChange = (field: string, value: string | number) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const [uploadedUrls, setUploadedUrls] = useState<{
    video?: string;
    thumbnail?: string;
    subtitle?: string;
  }>({});

  const handleFileUpload = async (field: 'video' | 'thumbnail' | 'subtitle', file: File) => {
    try {
      // Delete old file if it exists
      if (episode?.assets?.[field]) {
        try {
          const { deleteObject, ref } = await import('firebase/storage');
          const { storage } = await import('../lib/firebase');
          const oldFileRef = ref(storage, episode.assets[field]);
          await deleteObject(oldFileRef);
        } catch (deleteError) {
          // Silently fail - old file deletion is not critical
        }
      }
      
      const fileName = `${Date.now()}_${file.name}`;
      const storagePath = `episodes/${userId}/${contentId}/${episode?.id}`;
      const fileUrl = await uploadFileDirect(storagePath, file);
      
      // Store the uploaded URL
      setUploadedUrls(prev => {
        return { ...prev, [field]: fileUrl };
      });
      
      if (field === 'video') {
        setVideoFile(file);
        // Calculate duration for video files
        if (file.type.startsWith('video/')) {
          const duration = await getVideoDuration(file);
          setFormData(prev => ({ ...prev, duration }));
        }
      } else if (field === 'thumbnail') {
        setThumbnailFile(file);
      } else if (field === 'subtitle') {
        setSubtitleFile(file);
      }
      
      toast({
        title: 'File Uploaded',
        description: `${file.name} has been uploaded successfully`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Error uploading file:', error);
      toast({
        title: 'Upload Failed',
        description: 'Failed to upload file. Please try again.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const getVideoDuration = (file: File): Promise<number> => {
    return new Promise((resolve, reject) => {
      const video = document.createElement('video');
      video.preload = 'metadata';
      
      video.onloadedmetadata = () => {
        window.URL.revokeObjectURL(video.src);
        resolve(Math.round(video.duration / 60)); // Convert to minutes
      };
      
      video.onerror = () => {
        window.URL.revokeObjectURL(video.src);
        reject(new Error('Could not load video metadata'));
      };
      
      video.src = URL.createObjectURL(file);
    });
  };

  const handleSave = async () => {
    if (!episode) return;
    
    setIsSaving(true);
    try {
      // Get current content to update episodes array
      const { getContentForEdit } = await import('../services/contentUpdateService');
      const contentData = await getContentForEdit(contentId, userId);
      const currentEpisodes = contentData.episodes || [];
      
      // Update the specific episode
      const updatedEpisodes = currentEpisodes.map((ep: Episode) => {
        if (ep.id === episode.id) {
          const updatedEpisode = { 
            ...ep, 
            ...formData
          };
          
          // Always update assets structure for video files
          updatedEpisode.assets = {
            ...ep.assets,
            ...(uploadedUrls.video && { video: uploadedUrls.video }),
            ...(uploadedUrls.thumbnail && { thumbnail: uploadedUrls.thumbnail }),
            ...(uploadedUrls.subtitle && { subtitle: uploadedUrls.subtitle })
          };
          
          // Remove the old videoUrl field if it exists
          delete updatedEpisode.videoUrl;
          delete updatedEpisode.thumbnailUrl;
          delete updatedEpisode.subtitleUrl;
          
          return updatedEpisode;
        }
        return ep;
      });
      
      const result = await updateContent(contentId, userId, {
        episodes: updatedEpisodes
      });
      
      if (result.success) {
        toast({
          title: 'Episode Updated',
          description: 'Episode has been updated successfully',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        onSuccess();
        onClose();
      } else {
        toast({
          title: 'Update Failed',
          description: result.message,
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error('Error updating episode:', error);
      toast({
        title: 'Update Failed',
        description: 'An unexpected error occurred.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsSaving(false);
    }
  };

  if (!episode) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Edit Episode: {episode.title}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4} align="stretch">
            {/* Episode Details */}
            <VStack align="stretch" spacing={3}>
              <Text fontWeight="medium">Episode Details</Text>
              <Input
                placeholder="Episode Title"
                value={formData.title}
                onChange={(e) => handleInputChange('title', e.target.value)}
                bg={inputBg}
              />
              <Textarea
                placeholder="Episode Description"
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                bg={inputBg}
                rows={3}
              />
              <HStack spacing={4}>
                <Input
                  placeholder="Season Number"
                  type="number"
                  value={formData.seasonNumber}
                  onChange={(e) => handleInputChange('seasonNumber', parseInt(e.target.value) || 1)}
                  bg={inputBg}
                />
                <Input
                  placeholder="Episode Number"
                  type="number"
                  value={formData.episodeNumber}
                  onChange={(e) => handleInputChange('episodeNumber', parseInt(e.target.value) || 1)}
                  bg={inputBg}
                />
                <Input
                  placeholder="Duration (minutes)"
                  type="number"
                  value={formData.duration}
                  onChange={(e) => handleInputChange('duration', parseInt(e.target.value) || 0)}
                  bg={inputBg}
                />
              </HStack>
            </VStack>

            <Divider />

            {/* File Uploads */}
            <VStack align="stretch" spacing={3}>
              <Text fontWeight="medium">Episode Files</Text>
              
              {/* Video File */}
              <Box
                border="2px dashed"
                borderColor={videoFile ? 'green.400' : borderColor}
                borderRadius="md"
                p={3}
                textAlign="center"
                bg={videoFile ? 'green.50' : 'transparent'}
              >
                <VStack>
                  <IconWrapper icon={FiVideo} size={20} color={videoFile ? 'green.500' : 'gray.500'} />
                  {videoFile ? (
                    <>
                      <Text fontSize="xs" color="green.600">✓ Video uploaded</Text>
                      <Text fontSize="xs" color="green.500" isTruncated maxW="150px">
                        {videoFile.name}
                      </Text>
                    </>
                  ) : (
                    <Text fontSize="xs">Upload video</Text>
                  )}
                  <Button size="sm" onClick={() => document.getElementById('episode-video-upload')?.click()}>
                    {videoFile ? 'Change' : 'Choose'}
                  </Button>
                  <Input
                    type="file"
                    accept="video/*"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) handleFileUpload('video', file);
                    }}
                    display="none"
                    id="episode-video-upload"
                  />
                </VStack>
              </Box>

              {/* Thumbnail File */}
              <Box
                border="2px dashed"
                borderColor={thumbnailFile ? 'green.400' : borderColor}
                borderRadius="md"
                p={3}
                textAlign="center"
                bg={thumbnailFile ? 'green.50' : 'transparent'}
              >
                <VStack>
                  <IconWrapper icon={FiImage} size={20} color={thumbnailFile ? 'green.500' : 'gray.500'} />
                  {thumbnailFile ? (
                    <>
                      <Text fontSize="xs" color="green.600">✓ Thumbnail uploaded</Text>
                      <Text fontSize="xs" color="green.500" isTruncated maxW="150px">
                        {thumbnailFile.name}
                      </Text>
                    </>
                  ) : (
                    <Text fontSize="xs">Upload thumbnail</Text>
                  )}
                  <Button size="sm" onClick={() => document.getElementById('episode-thumbnail-upload')?.click()}>
                    {thumbnailFile ? 'Change' : 'Choose'}
                  </Button>
                  <Input
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) handleFileUpload('thumbnail', file);
                    }}
                    display="none"
                    id="episode-thumbnail-upload"
                  />
                </VStack>
              </Box>

              {/* Subtitle File */}
              <Box
                border="2px dashed"
                borderColor={subtitleFile ? 'green.400' : borderColor}
                borderRadius="md"
                p={3}
                textAlign="center"
                bg={subtitleFile ? 'green.50' : 'transparent'}
              >
                <VStack>
                  <IconWrapper icon={FiFileText} size={20} color={subtitleFile ? 'green.500' : 'gray.500'} />
                  {subtitleFile ? (
                    <>
                      <Text fontSize="xs" color="green.600">✓ Subtitle uploaded</Text>
                      <Text fontSize="xs" color="green.500" isTruncated maxW="150px">
                        {subtitleFile.name}
                      </Text>
                    </>
                  ) : (
                    <Text fontSize="xs">Upload subtitle</Text>
                  )}
                  <Button size="sm" onClick={() => document.getElementById('episode-subtitle-upload')?.click()}>
                    {subtitleFile ? 'Change' : 'Choose'}
                  </Button>
                  <Input
                    type="file"
                    accept=".srt,.vtt,.ass"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) handleFileUpload('subtitle', file);
                    }}
                    display="none"
                    id="episode-subtitle-upload"
                  />
                </VStack>
              </Box>
            </VStack>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>
            Cancel
          </Button>
          <Button colorScheme="blue" onClick={handleSave} isLoading={isSaving}>
            Save Changes
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
